import { collectionGroup, endBefore, getCountFromServer, getDocs, limit, limitToLast, query, startAfter, where, orderBy } from 'firebase/firestore';
import { toLower } from 'lodash';
import { isBooleanTrue, isListNotEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from '../utils/objectUtils';
import AuthUser from './AuthUser';
import { toE164PhoneNumber, toPhoneNumberLocal } from '../utils/stringFormatter';
import { db } from '../firebase';
import Address from './Address';
import { subCollectionCreate, subCollectionGetByParentIdAndSubId, subCollectionGetBySingleQuery, subCollectionRemove, subCollectionUpdate } from '../utils/dbUtils';

class CoveredEntityUser {
	static collectionName = 'coveredEntities';
	static subCollectionName = 'coveredEntityUsers';

	coveredEntityId = null;
	userId = null;

	email = null;
	phone = null;

	firstName = null;
	firstNameLower = null;
	middleName = null;
	lastName = null;
	lastNameLower = null;
	suffix = null;

	billingAddress = new Address(null).toDto();
	shippingAddress = new Address(null).toDto();

	roles = ['ADMIN'];

	phoneVerified = false;

	constructor(coveredEntityUser) {
		if (isObjectNotEmpty(coveredEntityUser)) {
			if (isStringNotEmpty(coveredEntityUser.coveredEntityId)) { this.coveredEntityId = coveredEntityUser.coveredEntityId; }
			if (isStringNotEmpty(coveredEntityUser.userId)) { this.userId = coveredEntityUser.userId; }

			if (isStringNotEmpty(coveredEntityUser.email)) { this.email = toLower(coveredEntityUser.email); }
			if (isStringNotEmpty(coveredEntityUser.phone)) { this.phone = toE164PhoneNumber(coveredEntityUser.phone); }

			if (isStringNotEmpty(coveredEntityUser.firstName)) {
				this.firstName = coveredEntityUser.firstName;
				this.firstNameLower = toLower(coveredEntityUser.firstName);
			}
			if (isStringNotEmpty(coveredEntityUser.lastName)) {
				this.lastName = coveredEntityUser.lastName;
				this.lastNameLower = toLower(coveredEntityUser.lastName);
			}
			if (isStringNotEmpty(coveredEntityUser.middleName)) { this.middleName = coveredEntityUser.middleName; }
			if (isStringNotEmpty(coveredEntityUser.suffix)) { this.suffix = coveredEntityUser.suffix; }

			if (isObjectNotEmpty(coveredEntityUser.billingAddress)) { this.billingAddress = new Address(coveredEntityUser.billingAddress).toDto(); }
			if (isObjectNotEmpty(coveredEntityUser.shippingAddress)) { this.shippingAddress = new Address(coveredEntityUser.shippingAddress).toDto(); }

			if (isListNotEmpty(coveredEntityUser.roles)) { this.roles = coveredEntityUser.roles; }

			if (isBooleanTrue(coveredEntityUser.phoneVerified)) { this.phoneVerified = true; }
		}
	}

	toDto = () => {
		return {
			coveredEntityId: this.coveredEntityId,
			userId: this.userId,

			email: this.email,
			phone: this.phone,

			firstName: this.firstName,
			firstNameLower: this.firstNameLower,
			middleName: this.middleName,
			lastName: this.lastName,
			lastNameLower: this.lastNameLower,
			suffix: this.suffix,

			billingAddress: this.billingAddress,
			shippingAddress: this.shippingAddress,

			roles: this.roles,

			phoneVerified: this.phoneVerified,
		};
	}

	isValid = () => {
		if (isStringEmpty(this.userId)) {
			throw new Error('userId is required.');
		}

		if (isStringEmpty(this.coveredEntityId)) {
			throw new Error('coveredEntityId is required.');
		}

		return true;
	}

	create = async () => {
		if (this.isValid()) {
			const existingCoveredEntityUser = await CoveredEntityUser.getByCoveredEntityIdAndUserId(this.coveredEntityId, this.userId);
			if (isObjectNotEmpty(existingCoveredEntityUser)) {
				const success = await AuthUser.updateCoveredEntityClaims(this.coveredEntityId, this.userId, this.roles);
				if (isBooleanTrue(success)) {
					return { ...existingCoveredEntityUser, roles: this.roles };
				}
			} else {
				const newCoveredEntityUser = await subCollectionCreate(CoveredEntityUser.collectionName, this.coveredEntityId, CoveredEntityUser.subCollectionName, this.userId, this.toDto());
				if (isObjectNotEmpty(newCoveredEntityUser)) {
					await AuthUser.updateCoveredEntityClaims(this.coveredEntityId, this.userId, this.roles);

					return newCoveredEntityUser;
				}
			}
		}

		return null;
	}

	static update = async (coveredEntityId, userId, updatedCoveredEntityUser) => {
		const updatedObj = new CoveredEntityUser(updatedCoveredEntityUser);
		if (updatedObj.isValid() && coveredEntityId === updatedObj.coveredEntityId && userId === updatedObj.userId) {
			const success = await subCollectionUpdate(CoveredEntityUser.collectionName, updatedObj.coveredEntityId, CoveredEntityUser.subCollectionName, updatedObj.userId, updatedObj.toDto());
			if (isBooleanTrue(success)) {
				await AuthUser.updateCoveredEntityClaims(updatedObj.coveredEntityId, updatedObj.userId, updatedObj.roles);

				return true;
			}
		} else {
			throw new Error('coveredEntityId and userId must match that of the updated covered entity user.')
		}

		return false;
	}

	static delete = async (coveredEntityId, userId) => {
		return await subCollectionRemove(CoveredEntityUser.collectionName, coveredEntityId, CoveredEntityUser.subCollectionName, userId);
	}

	static getByCoveredEntityIdAndUserId = async (coveredEntityId, userId) => {
		return await subCollectionGetByParentIdAndSubId(CoveredEntityUser.collectionName, coveredEntityId, CoveredEntityUser.subCollectionName, userId);
	}

	static getByUserId = async (userId) => {
		return await subCollectionGetBySingleQuery(CoveredEntityUser.subCollectionName, 'userId', userId, '==');
	}

	static getByQueryConstraints = async (queryConstraints = []) => {
		let q = query(collectionGroup(db, CoveredEntityUser.subCollectionName), ...queryConstraints);

		const snapShot = await getDocs(q);
		// console.log(snapShot)
		return snapShot.docs.map((doc) => {
			return {
				id: doc.id, // append document id to each document
				...doc.data()
			};
		});
	}

	static getByPhone = async (phone) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(phone)) {
			const cleanPhone = toPhoneNumberLocal(phone);
			const formattedPhone = toE164PhoneNumber(phone);
			queryConstraints.push(where('phone', 'in', [phone, formattedPhone, cleanPhone]));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getByEmail = async (email) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(email)) {
			queryConstraints.push(where('email', '==', toLower(email)));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getByCoveredEntityIdAndPhone = async (coveredEntityId, phone) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
		}
		if (isStringNotEmptyOrZeroLength(phone)) {
			const cleanPhone = toPhoneNumberLocal(phone);
			const formattedPhone = toE164PhoneNumber(phone);
			queryConstraints.push(where('phone', 'in', [phone, formattedPhone, cleanPhone]));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getByCoveredEntityIdAndEmail = async (coveredEntityId, email) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
		}
		if (isStringNotEmptyOrZeroLength(email)) {
			queryConstraints.push(where('email', '==', toLower(email)));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getByCoveredEntityIdAndFirstNameAndLastNameAndEmailAndPhone = async (coveredEntityId, firstName, lastName, email, phone) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
		}
		if (isStringNotEmptyOrZeroLength(firstName)) {
			queryConstraints.push(where('firstNameLower', '==', toLower(firstName)));
		}
		if (isStringNotEmptyOrZeroLength(lastName)) {
			queryConstraints.push(where('lastNameLower', '==', toLower(lastName)));
		}
		if (isStringNotEmptyOrZeroLength(email)) {
			queryConstraints.push(where('email', '==', toLower(email)));
		}
		if (isStringNotEmptyOrZeroLength(phone)) {
			const cleanPhone = toPhoneNumberLocal(phone);
			const formattedPhone = toE164PhoneNumber(phone);
			queryConstraints.push(where('phone', 'in', [phone, formattedPhone, cleanPhone]));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getCount = async (queryParameters) => {
		const { coveredEntityId, role } = queryParameters;

		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
		}

		if (isListNotEmpty(role)) {
			queryConstraints.push(where('roles', 'array-contains-any', role));
		} else if (isStringNotEmptyOrZeroLength(role)) {
			queryConstraints.push(where('roles', 'array-contains', role));
		}


		let q = query(collectionGroup(db, CoveredEntityUser.subCollectionName), ...queryConstraints);

		const snapShot = await getCountFromServer(q);
		//console.log(snapShot.data());

		return snapShot.data().count;
	}

	static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
		const { coveredEntityId, role } = queryParameters;

		let queryConstraints = [];

		if (isListNotEmpty(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', 'in', coveredEntityId));
		} else if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
		}

		if (isListNotEmpty(role)) {
			queryConstraints.push(where('roles', 'array-contains-any', role));
		} else if (isStringNotEmptyOrZeroLength(role)) {
			queryConstraints.push(where('roles', 'array-contains', role));
		}

		queryConstraints.push(orderBy(orderByField, 'asc'));

		if (isStringNotEmpty(firstVisible)) {
			queryConstraints.push(limitToLast(pageSize));
			queryConstraints.push(endBefore(lastVisible[orderByField]));
		} else if (isStringNotEmpty(lastVisible)) {
			queryConstraints.push(limit(pageSize));
			queryConstraints.push(startAfter(lastVisible[orderByField]));
		} else {
			queryConstraints.push(limit(pageSize));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static get = async (queryParameters) => {
		let allRecords = [];
		try {
			let next = null;

			let totalCount = await this.getCount(queryParameters);
			let totalPages = Math.ceil(totalCount / 10000.0);
			// console.log('totalCount: ' + totalCount);
			// console.log('totalPages: ' + totalPages);
			let pages = [];
			for (let i = 0; i < totalPages; i++) {
				pages.push(i + 1);
			}
			for (const page of pages) {
				// console.log('starting page ' + page);

				let list = await this.getPaginated(queryParameters, 10000, null, next, 'createdAt');

				// console.log(list.length);
				allRecords = [...allRecords, ...list];

				// console.log(allRecords.length);

				// console.log('go to next page');
				next = list[list.length - 1];
			}
		} catch (error) {
			console.log(error);
		}

		return allRecords;
	}
}

export default CoveredEntityUser;